import React, { useMemo, useState } from 'react';
import { View, StyleSheet, processColor } from 'react-native';
import Animated, { and, blue, cond, divide, Extrapolate, greaterOrEq, green, interpolateColors, interpolateNode, lessOrEq, opacity as alpha, red, rgbaColor, sub, useValue, } from 'react-native-reanimated';
import { GestureHandler } from './gestureHandler';
export const Picker = ({ coloredOpacity, colorStops = [], containerStyle, defaultValue, disabled = false, dividerStyle, doScrollToIndex, fontFamilies = [], items, itemHeight, labelStyle, onChange, visibleItems = 5, withOpacity, withScale, testID, }) => {
    const [currentSelectedIndex, setCurrentSelectedIndex] = useState();
    const [currentSelectedValue, setCurrentSelectedValue] = useState();
    const translateY = useValue(0);
    const roundedItems = Math.floor(visibleItems / 2);
    const renderItems = useMemo(() => (React.createElement(Animated.View, { style: { transform: [{ translateY }] } }, items.map((v, i) => {
        const transform = [];
        const node = divide(sub(translateY, itemHeight * roundedItems), -itemHeight);
        const colorStopValues = colorStops.map((c) => {
            const val = processColor(c);
            return rgbaColor(red(val), green(val), blue(val), alpha(val));
        });
        const opacity = !withOpacity
            ? 1
            : interpolateNode(node, {
                inputRange: [i - roundedItems, i, i + roundedItems],
                outputRange: [0.2, 1, 0.2],
                extrapolate: Extrapolate.CLAMP,
            });
        if (withScale) {
            const scale = interpolateNode(node, {
                inputRange: [i - roundedItems, i, i + roundedItems],
                outputRange: [2 / 3, 1, 2 / 3],
                extrapolate: Extrapolate.CLAMP,
            });
            transform.push({ scale });
        }
        const lableColor = !coloredOpacity && colorStopValues.length === 0
            ? labelStyle?.color
            : interpolateColors(node, {
                inputRange: [i - 2, i - 1, i, i + 1, i + 2],
                outputColorRange: colorStopValues,
            });
        const fontFamily = fontFamilies.length > 1
            ? cond(and(greaterOrEq(node, i - 1 / 3), lessOrEq(node, i + 1 / 3)), fontFamilies[0], fontFamilies[1])
            : fontFamilies[0];
        return (React.createElement(Animated.View, { key: v.value, style: [
                styles.item,
                {
                    height: itemHeight,
                    opacity,
                    transform,
                },
            ] },
            React.createElement(Animated.Text, { style: [styles.label, labelStyle, { fontFamily, color: lableColor }] }, v.label)));
    }))), [itemHeight, visibleItems, withOpacity, withScale, coloredOpacity, colorStops]);
    const onValueChange = (newValue, index) => {
        setCurrentSelectedIndex(index);
        setCurrentSelectedValue(newValue);
        if (onChange != null && typeof onChange === 'function') {
            onChange(newValue, index);
        }
    };
    return (React.createElement(View, { testID: testID, accessibilityRole: "adjustable", accessibilityLiveRegion: "polite", accessibilityLabel: `value: ${currentSelectedValue ?? 'none'}, index: ${currentSelectedIndex ?? 'none'}`, accessibilityValue: {
            min: 0,
            max: items?.length ?? 0,
            now: currentSelectedIndex,
        }, style: [styles.container, containerStyle, { height: itemHeight * visibleItems }] },
        React.createElement(View, { style: StyleSheet.absoluteFill },
            React.createElement(View, { style: [
                    {
                        top: itemHeight * roundedItems,
                        height: itemHeight,
                    },
                    dividerStyle,
                ] })),
        renderItems,
        React.createElement(GestureHandler, { defaultValue: defaultValue, disabled: disabled, doScrollToIndex: doScrollToIndex, itemHeight: itemHeight, max: items.length, onValueChange: onValueChange, value: translateY, values: items, visibleItems: visibleItems })));
};
const styles = StyleSheet.create({
    container: {
        width: '100%',
        overflow: 'hidden',
    },
    item: {
        justifyContent: 'center',
    },
    label: {
        color: '#000000',
        fontWeight: '500',
        fontSize: 24,
        textAlign: 'center',
        textAlignVertical: 'center',
    },
});
